import { z } from 'zod';
import {
  I18nStringSchema,
  MoneySchema,
  ShortIdSchema,
  TimestampSchema,
  UidSchema,
} from './primitives.js';

const ScheduleTriggerSchema = z.object({
  kind: z.literal('schedule'),
  dows: z.array(z.number().int().min(0).max(6)).optional(),
  timeWindow: z
    .object({ from: z.string().regex(/^\d{2}:\d{2}$/), to: z.string().regex(/^\d{2}:\d{2}$/) })
    .optional(),
  dateRange: z.object({ from: TimestampSchema, to: TimestampSchema }).optional(),
  timezone: z.string().default('Asia/Kuala_Lumpur'),
});

const LifecycleTriggerSchema = z.object({
  kind: z.literal('lifecycle'),
  event: z.enum(['first_visit', 'nth_visit', 'inactive_days', 'birthday_window']),
  n: z.number().int().positive().optional(),
});

const AudienceTriggerSchema = z.object({
  kind: z.literal('audience'),
  segmentId: ShortIdSchema.optional(),
  ad_hoc: z.record(z.string(), z.unknown()).optional(),
});

const ManualTriggerSchema = z.object({
  kind: z.literal('manual'),
  code: z.string().min(3).max(20).describe('Customer types this to unlock'),
});

const AlwaysTriggerSchema = z.object({ kind: z.literal('always') });

export const PromotionTriggerSchema = z.discriminatedUnion('kind', [
  ScheduleTriggerSchema,
  LifecycleTriggerSchema,
  AudienceTriggerSchema,
  ManualTriggerSchema,
  AlwaysTriggerSchema,
]);
export type PromotionTrigger = z.infer<typeof PromotionTriggerSchema>;

export const PromotionConditionSchema = z.object({
  minSpend: MoneySchema.optional(),
  mustIncludeServiceIds: z.array(z.string()).optional(),
  mustIncludeAddOnIds: z.array(z.string()).optional(),
  vehicleTypeIdIn: z.array(z.string()).optional(),
  customerTagIn: z.array(z.string()).optional(),
  outletIdIn: z.array(z.string()).optional(),
  newCustomerOnly: z.boolean().optional(),
  firstNRedemptionsToday: z.number().int().positive().optional(),
});
export type PromotionCondition = z.infer<typeof PromotionConditionSchema>;

export const PromotionEffectSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('pct_off'),
    value: z.number().min(0).max(100),
    appliesTo: z.enum(['cart', 'specificService', 'cheapestItem', 'mostExpensiveItem']),
  }),
  z.object({ type: z.literal('fixed_off'), value: MoneySchema, appliesTo: z.literal('cart') }),
  z.object({ type: z.literal('free_service'), serviceId: z.string() }),
  z.object({ type: z.literal('replace_price'), packageId: z.string(), newPrice: MoneySchema }),
  z.object({ type: z.literal('bonus_points'), points: z.number().int().positive() }),
  z.object({ type: z.literal('free_add_on'), addOnId: z.string() }),
]);
export type PromotionEffect = z.infer<typeof PromotionEffectSchema>;

export const PromotionStackingSchema = z.object({
  withVouchers: z.enum(['allow', 'block', 'bestOnly']).default('bestOnly'),
  withOtherPromotions: z.enum(['allow', 'block', 'bestOnly']).default('bestOnly'),
});

export const PromotionLimitsSchema = z.object({
  totalRedemptions: z.number().int().positive().optional(),
  perCustomer: z.number().int().positive().optional(),
  perDay: z.number().int().positive().optional(),
});

export const PromotionSchema = z.object({
  id: ShortIdSchema,
  name: I18nStringSchema,
  description: I18nStringSchema.optional(),
  bannerImageUrl: z.string().url().optional(),
  status: z.enum(['draft', 'active', 'paused', 'archived']),
  templateKey: z.string().optional().describe('e.g., "happy_hour", "day_of_week"'),
  triggers: z.array(PromotionTriggerSchema).min(1),
  conditions: PromotionConditionSchema.default({}),
  effect: PromotionEffectSchema,
  stacking: PromotionStackingSchema,
  limits: PromotionLimitsSchema.default({}),
  validity: z.object({ from: TimestampSchema, to: TimestampSchema }),
  version: z.number().int().nonnegative().default(0),
  createdBy: UidSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Promotion = z.infer<typeof PromotionSchema>;

// NOTE: the promotion-application AUDIT record (the Phase-2 write that lands the
// `promotionApplications` collection) lives in its own schema file —
// `promotion-application.ts` → `PromotionApplicationSchema`. It carries the
// full audit context (tenantId/outletId/ticketNumber/customerId + integer
// `savingsSen`) and a deterministic idempotent doc id, and is written
// atomically by `bookTicket`/`appendItemsToTicket`. The earlier stub here was
// never wired to a reader or writer and has been superseded.
